'use client';

// Constants
import { STATUS_CODE, STATUS_MESSAGES } from '@shared/constants';
import { AUTH_ERROR_MESSAGES } from '@/constants/auth';

// Store
import { appStoreActions } from '@/stores';

// Providers
import { useAuthActions } from '@/providers/auth';

type ActionResult<T> = {
  data: T;
  error: unknown;
};

export const errorHandler = <TArgs extends unknown[], TReturn>(
  actionFn: (...args: TArgs) => Promise<ActionResult<TReturn>>,
  signOut: () => Promise<void>,
) => {
  return async (...args: TArgs): Promise<ActionResult<TReturn> | null> => {
    const { data, error } = await actionFn(...args);

    if (error && error === STATUS_MESSAGES[STATUS_CODE.UNAUTHORIZED]) {
      appStoreActions.setAuthorized(false);
      appStoreActions.setForceLogoutWithMessage(
        AUTH_ERROR_MESSAGES.ACCOUNT_DEACTIVATED,
      );
      await signOut();
      return null;
    }

    if (error && error === AUTH_ERROR_MESSAGES.ROLE_MISMATCH) {
      appStoreActions.setForceLogoutWithMessage(error);
      return null;
    }

    return { data, error };
  };
};

/**
 * Custom hook to wrap API actions with auth error handling
 * @returns Object containing the withAuth function
 */
export const useAuthAction = () => {
  const { handleSignOut } = useAuthActions();

  const withAuth = <TArgs extends unknown[], TReturn>(
    actionFn: (...args: TArgs) => Promise<ActionResult<TReturn>>,
  ) => {
    return errorHandler(actionFn, handleSignOut);
  };

  return { withAuth };
};
